const GITHUB_API_URL = "https://api.github.com/graphql";
export const REPO_OWNER = "rajvirsinghbenipal";
export const REPO_NAME = "VoiceUS";

// Recursively fetch all .md files in a folder and its subfolders
export async function fetchMarkdownFilesRecursive(folder = "content") {
  const query = `
    query getFolder($owner: String!, $repo: String!, $folder: String!) {
      repository(owner: $owner, name: $repo) {
        object(expression: $folder) {
          ... on Tree {
            entries {
              name
              type
            }
          }
        }
      }
    }
  `;
  const variables = { owner: REPO_OWNER, repo: REPO_NAME, folder: `main:${folder}` };
  const response = await makeGraphQLRequest(query, variables);

  if (
    !response.repository.object ||
    !response.repository.object.entries
  ) {
    return [];
  }

  let files = [];
  for (const entry of response.repository.object.entries) {
    if (entry.type === "blob" && entry.name.endsWith(".md")) {
      files.push({ path: `${folder}/${entry.name}`, name: entry.name });
    } else if (entry.type === "tree") {
      // Recursively fetch from subfolder
      const subFiles = await fetchMarkdownFilesRecursive(`${folder}/${entry.name}`);
      files = files.concat(subFiles);
    }
  }
  return files;
}

export async function fetchMarkdownFileContent(path) {
  const query = `
    query getFileContent($owner: String!, $repo: String!, $path: String!) {
      repository(owner: $owner, name: $repo) {
        object(expression: $path) {
          ... on Blob {
            text
          }
        }
      }
    }
  `;
  const variables = { owner: REPO_OWNER, repo: REPO_NAME, path: `main:${path}` };
  const response = await makeGraphQLRequest(query, variables);
  if (!response.repository.object) {
    return 'File not found or unable to fetch content.';
  }
  return response.repository.object.text;
}

export async function createMarkdownFile(path, content) {
  // Get latest commit SHA
  const branchQuery = `
    query getBranchOid($owner: String!, $repo: String!, $branch: String!) {
      repository(owner: $owner, name: $repo) {
        ref(qualifiedName: $branch) {
          target {
            ... on Commit {
              oid
            }
          }
        }
      }
    }
  `;
  const branchVariables = {
    owner: REPO_OWNER,
    repo: REPO_NAME,
    branch: "refs/heads/main"
  };
  const branchResponse = await makeGraphQLRequest(branchQuery, branchVariables);
  const latestOid = branchResponse.repository.ref.target.oid;

  // Mutation for creating a file
  const mutation = `
    mutation createCommit($input: CreateCommitOnBranchInput!) {
      createCommitOnBranch(input: $input) {
        commit { oid }
      }
    }
  `;
  const input = {
    branch: {
      repositoryNameWithOwner: `${REPO_OWNER}/${REPO_NAME}`,
      branchName: "main"
    },
    message: { headline: `Create ${path}` },
    fileChanges: {
      additions: [
        {
          path,
          contents: btoa(unescape(encodeURIComponent(content)))
        }
      ]
    },
    expectedHeadOid: latestOid
  };
  await makeGraphQLRequest(mutation, { input });
}

export async function deleteMarkdownFile(path) {
  // Get latest commit SHA
  const branchQuery = `
    query getBranchOid($owner: String!, $repo: String!, $branch: String!) {
      repository(owner: $owner, name: $repo) {
        ref(qualifiedName: $branch) {
          target {
            ... on Commit {
              oid
            }
          }
        }
      }
    }
  `;
  const branchVariables = {
    owner: REPO_OWNER,
    repo: REPO_NAME,
    branch: "refs/heads/main"
  };
  const branchResponse = await makeGraphQLRequest(branchQuery, branchVariables);
  const latestOid = branchResponse.repository.ref.target.oid;

  // Mutation for deleting a file
  const mutation = `
    mutation deleteCommit($input: CreateCommitOnBranchInput!) {
      createCommitOnBranch(input: $input) {
        commit { oid }
      }
    }
  `;
  const input = {
    branch: {
      repositoryNameWithOwner: `${REPO_OWNER}/${REPO_NAME}`,
      branchName: "main"
    },
    message: { headline: `Delete ${path}` },
    fileChanges: {
      deletions: [
        { path }
      ]
    },
    expectedHeadOid: latestOid
  };
  await makeGraphQLRequest(mutation, { input });
}


export async function saveMarkdownFile(path, content) {
  // 1. Get the latest commit SHA (oid) for the branch
  const branchQuery = `
    query getBranchOid($owner: String!, $repo: String!, $branch: String!) {
      repository(owner: $owner, name: $repo) {
        ref(qualifiedName: $branch) {
          target {
            ... on Commit {
              oid
            }
          }
        }
      }
    }
  `;
  const branchVariables = {
    owner: REPO_OWNER,
    repo: REPO_NAME,
    branch: "refs/heads/main"
  };
  const branchResponse = await makeGraphQLRequest(branchQuery, branchVariables);
  const latestOid = branchResponse.repository.ref.target.oid;

  // 2. Prepare the mutation
  const mutation = `
    mutation createCommit($input: CreateCommitOnBranchInput!) {
      createCommitOnBranch(input: $input) {
        commit {
          oid
        }
      }
    }
  `;

  // 3. Prepare the input object
  const input = {
    branch: {
      repositoryNameWithOwner: `${REPO_OWNER}/${REPO_NAME}`,
      branchName: "main"
    },
    message: {
      headline: `Update ${path}`
    },
    fileChanges: {
      additions: [
        {
          path,
          contents: btoa(unescape(encodeURIComponent(content))) // base64 encode
        }
      ]
    },
    expectedHeadOid: latestOid
  };

  await makeGraphQLRequest(mutation, { input });
}

export async function fetchImageFolders(root = "static/images") {
  const query = `
    query getFolders($owner: String!, $repo: String!, $root: String!) {
      repository(owner: $owner, name: $repo) {
        object(expression: $root) {
          ... on Tree {
            entries {
              name
              type
            }
          }
        }
      }
    }
  `;
  const variables = { owner: REPO_OWNER, repo: REPO_NAME, root: `main:${root}` };
  const response = await makeGraphQLRequest(query, variables);

  if (!response.repository.object || !response.repository.object.entries) return [];
  return response.repository.object.entries
    .filter(e => e.type === "tree")
    .map(e => e.name);
}

export async function fetchImagesInFolder(folder = "static/images") {
  const query = `
    query getImages($owner: String!, $repo: String!, $folder: String!) {
      repository(owner: $owner, name: $repo) {
        object(expression: $folder) {
          ... on Tree {
            entries {
              name
              type
            }
          }
        }
      }
    }
  `;
  const variables = { owner: REPO_OWNER, repo: REPO_NAME, folder: `main:${folder}` };
  const response = await makeGraphQLRequest(query, variables);

  if (!response.repository.object || !response.repository.object.entries) return [];
  return response.repository.object.entries
    .filter(e => e.type === "blob" && /\.(png|jpe?g|gif|webp|svg)$/i.test(e.name))
    .map(e => ({ path: `${folder}/${e.name}`, name: e.name }));
}

export async function deleteImage(path) {
  // Same as deleteMarkdownFile, just pass the image path
  return deleteMarkdownFile(path);
}

export async function uploadImageToFolder(folder, file) {
  // Read file as base64
  const base64 = await new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result.split(',')[1]);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

  // Get latest commit SHA
  const branchQuery = `
    query getBranchOid($owner: String!, $repo: String!, $branch: String!) {
      repository(owner: $owner, name: $repo) {
        ref(qualifiedName: $branch) {
          target {
            ... on Commit {
              oid
            }
          }
        }
      }
    }
  `;
  const branchVariables = {
    owner: REPO_OWNER,
    repo: REPO_NAME,
    branch: "refs/heads/main"
  };
  const branchResponse = await makeGraphQLRequest(branchQuery, branchVariables);
  const latestOid = branchResponse.repository.ref.target.oid;

  const mutation = `
    mutation uploadImage($input: CreateCommitOnBranchInput!) {
      createCommitOnBranch(input: $input) {
        commit { oid }
      }
    }
  `;
  const path = `${folder}/${file.name}`;
  const input = {
    branch: {
      repositoryNameWithOwner: `${REPO_OWNER}/${REPO_NAME}`,
      branchName: "main"
    },
    message: { headline: `Upload image ${path}` },
    fileChanges: {
      additions: [
        {
          path,
          contents: base64
        }
      ]
    },
    expectedHeadOid: latestOid
  };
  await makeGraphQLRequest(mutation, { input });
}

async function makeGraphQLRequest(query, variables = {}) {
  const token = process.env.REACT_APP_GITHUB_PAT; // Set this in your .env file
  const response = await fetch(GITHUB_API_URL, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${token}`,
      "User-Agent": "my-custom-cms",
      "Content-Type": "application/json",
      Accept: "application/json",
    },
    body: JSON.stringify({ query, variables }),
  });

  const data = await response.json();

  if (response.status !== 200 || data.errors) {
    console.error("GraphQL API Error:", data.errors || data);
    throw new Error("GraphQL request failed");
  }

  return data.data;
}